/**
 * ReproductiveHistoryModal — репродуктивна анамнеза.
 * Данните, които моделите за оценка на риска искат (първа менструация, раждания,
 * кърмене, менопауза, хормонално лечение). Приложението не изчислява моделите.
 * Данни: GET/POST /api/my-data/reproductive
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Baby, X, Loader2, Info } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const NUMS = [
  { k: "menarche_age", l: "Възраст на първата менструация", p: "напр. 13" },
  { k: "parity", l: "Брой раждания", p: "напр. 2" },
  { k: "first_birth_age", l: "Възраст при първото раждане", p: "напр. 27" },
  { k: "breastfeeding_months", l: "Кърмене общо (месеци)", p: "напр. 14" },
  { k: "menopause_age", l: "Възраст на менопаузата", p: "напр. 51" },
  { k: "hrt_years", l: "Хормонално лечение (години)", p: "напр. 3" },
];

export default function ReproductiveHistoryModal({ open, onClose, onSaved }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/my-data/reproductive`);
      setD(data);
      setForm(Object.fromEntries(
        Object.entries(data.data || {}).filter(([, v]) => v !== null && v !== undefined)));
    } catch { setD(null); }
  }, [API]);

  useEffect(() => { if (open) load(); }, [open, load]);
  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === "" || v === null || v === undefined) return;
        payload[k] = NUMS.some((n) => n.k === k) ? Number(v) : v;
      });
      const { data } = await axios.post(`${API}/my-data/reproductive`, payload);
      setD(data);
      toast.success("Записано");
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg
        || e?.response?.data?.detail || "Записването не успя");
    } finally { setSaving(false); }
  };

  const panel = isDark ? "bg-slate-900 border-slate-700 text-slate-100"
                       : "bg-white border-slate-200 text-slate-900";
  const field = `w-full px-2.5 py-2 rounded-lg text-[12px] border ${
    isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`;

  const selects = [
    { k: "menopause_status", l: "Менструален статус", opts: d?.options?.menopause || [] },
    { k: "hrt_status", l: "Хормонозаместително лечение", opts: d?.options?.hrt || [] },
    { k: "oc_status", l: "Противозачатъчни таблетки", opts: d?.options?.oc || [] },
  ];

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center
                    bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      data-testid="reproductive-modal">
      <div className={`w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl
                       sm:rounded-2xl border shadow-2xl ${panel}`}>
        <div className={`sticky top-0 flex items-start justify-between gap-3 p-4 border-b ${
          isDark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"}`}>
          <div className="flex items-center gap-2 min-w-0">
            <Baby className="w-5 h-5 text-pink-500 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base font-extrabold">Репродуктивна анамнеза</h2>
              <p className="text-[11px] opacity-70">
                {d ? `${d.filled} от ${d.total} отговора` : "зарежда се…"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10"
            data-testid="reproductive-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className={`p-3 rounded-xl text-[11px] leading-snug border-l-[3px]
                           border-cyan-500 ${isDark ? "bg-cyan-500/10" : "bg-cyan-50"}`}
            data-testid="reproductive-why">
            {d?.why_bg}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {selects.map((s) => (
              <label key={s.k} className="block sm:col-span-2">
                <span className="text-[10px] font-bold uppercase opacity-60">{s.l}</span>
                <select value={form[s.k] || ""} onChange={(e) => set(s.k, e.target.value)}
                  className={field} data-testid={`reproductive-${s.k}`}>
                  <option value="">— изберете —</option>
                  {s.opts.map((o) => (
                    <option key={o.value} value={o.value}>{o.label_bg}</option>
                  ))}
                </select>
              </label>
            ))}
            {NUMS.map((n) => (
              <label key={n.k} className="block">
                <span className="text-[10px] font-bold uppercase opacity-60">{n.l}</span>
                <input value={form[n.k] ?? ""} onChange={(e) => set(n.k, e.target.value)}
                  inputMode="numeric" placeholder={n.p} className={field}
                  data-testid={`reproductive-${n.k}`} />
              </label>
            ))}
          </div>

          <button onClick={save} disabled={saving}
            className="w-full px-4 py-2.5 rounded-xl text-sm font-bold text-white
                       bg-gradient-to-r from-pink-500 to-rose-600 hover:opacity-95
                       disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="reproductive-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Запази
          </button>

          {!!d?.notes_bg?.length && (
            <div className="space-y-1.5" data-testid="reproductive-notes">
              <div className="text-[10px] font-bold uppercase opacity-60">
                Какво означава това
              </div>
              {d.notes_bg.map((n, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[11px]">
                  <Info className="w-3.5 h-3.5 text-cyan-500 shrink-0 mt-0.5" />
                  <span>{n}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
